import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { environment } from '@environments/environment';
import { MICROSERVICES } from '@shared/constants/microservices';
import { match } from 'path-to-regexp';
import { delay as rxjsDelay } from 'rxjs';
import { MOCK_RULES } from '../mock-api.config';

interface MockMatch {
    serviceKey: keyof typeof MICROSERVICES;
    path: string;
    headers?: string[];
    xAppId?: string;
    delay?: number;
}

const getServicePath = (serviceKey: keyof typeof MICROSERVICES): string => {
    const value = MICROSERVICES[serviceKey];
    return value.startsWith('/') ? value : `/${value}`;
};

const findMockMatch = (url: string, method: string): MockMatch | null => {
    for (const rule of MOCK_RULES) {
        const servicePath = getServicePath(rule.serviceKey);
        const realBase = `${environment.url}${servicePath}`;
        const mockBase = `${environment.mockBaseUrl}${servicePath}`;

        const matchResult = matchPath(url, realBase, rule.paths, method) ?? matchPath(url, mockBase, rule.paths, method);
        if (matchResult) {
            return { serviceKey: rule.serviceKey, path: matchResult.path, headers: matchResult.headers, xAppId: matchResult.xAppId, delay: matchResult.delay };
        }
    }

    return null;
};

const matchPath = (
    url: string,
    base: string,
    paths: { path: string; method?: string; headers?: string[]; xAppId?: string; delay?: number }[],
    method: string
): { path: string; headers?: string[]; xAppId?: string; delay?: number } | null => {
    if (!url.startsWith(base)) {
        return null;
    }

    const path = url.substring(base.length);
    const pathRule = paths.find((rule) => {
        const matcher = match(rule.path, { decode: decodeURIComponent, end: true });
        const pathMatches = matcher(path) !== false;
        const methodMatches = !rule.method || rule.method === method;
        return pathMatches && methodMatches;
    });

    if (!pathRule) {
        return null;
    }

    return { path, headers: pathRule.headers, xAppId: pathRule.xAppId, delay: pathRule.delay };
};

const callCounters = new Map<string, number>();

export const mockRoutingInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    if (!environment.usePrismMocks) {
        return next(req);
    }

    const mockMatch = findMockMatch(req.url, req.method);

    if (!mockMatch) {
        return next(req);
    }

    let headers = req.headers;

    if (mockMatch.headers && mockMatch.headers.length > 0) {
        const counterKey = `${mockMatch.serviceKey}:${mockMatch.path}:${req.method}`;
        const currentCount = callCounters.get(counterKey) ?? 0;
        const headerIndex = Math.min(currentCount, mockMatch.headers.length - 1);
        const selectedHeader = mockMatch.headers[headerIndex];
        headers = headers.set('Prefer', selectedHeader);
        callCounters.set(counterKey, currentCount + 1);
        console.log(`[MOCK SEQUENCE] ${counterKey} call #${currentCount + 1} -> ${selectedHeader}`);
    }

    if (mockMatch.xAppId) {
        headers = headers.set('x-app-id', mockMatch.xAppId);
    }

    const servicePath = getServicePath(mockMatch.serviceKey);
    const mockBaseUrl = `${environment.mockBaseUrl}${servicePath}`;
    const url = req.url.startsWith(mockBaseUrl) ? req.url : `${mockBaseUrl}${mockMatch.path}`;

    console.log(`[MOCK ROUTING] ${req.method} ${req.url} -> ${url}`);

    const response$ = next(req.clone({ url, headers }));

    if (mockMatch.delay && mockMatch.delay > 0) {
        console.log(`[MOCK DELAY] ${req.method} ${url} delaying ${mockMatch.delay}ms`);
        return response$.pipe(rxjsDelay(mockMatch.delay));
    }

    return response$;
};
